<template>
  <div
    v-if="open"
    class="hierarchy-overlay"
    role="dialog"
    aria-modal="true"
    aria-label="Project hierarchy map"
    @click.self="close"
  >
    <div class="hierarchy-card">
      <div class="hierarchy-header">
        <div>
          <h3>Project Hierarchy</h3>
          <p>{{ projectName }}</p>
        </div>
        <div class="header-actions">
          <button class="btn-secondary" type="button" @click="resetView">
            Reset view
          </button>
          <span class="zoom-indicator">Zoom: {{ zoomPercent }}%</span>
          <button class="icon-btn" type="button" @click="close" aria-label="Close hierarchy map">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>
      </div>

      <div class="hierarchy-body" ref="layoutRef">
        <div v-if="loading" class="state">Loading hierarchy...</div>
        <div v-else-if="error" class="state error">{{ error }}</div>
        <template v-else>
          <div
            ref="sceneFrameRef"
            class="scene-frame"
            :class="{ 'is-panning': Boolean(panDragState), 'is-interacting': isInteracting }"
            @mousedown="startPanDrag"
            @wheel.prevent="handleWheelZoom"
          >
            <div class="scene-root" :style="sceneRootStyle">
              <svg
                v-if="hasWorkflows"
                class="connector-overlay"
                :width="overlaySize.width"
                :height="overlaySize.height"
                :viewBox="`0 0 ${overlaySize.width} ${overlaySize.height}`"
                preserveAspectRatio="none"
              >
                <line
                  v-for="line in connectorLines"
                  :key="line.id"
                  :x1="line.x1"
                  :y1="line.y1"
                  :x2="line.x2"
                  :y2="line.y2"
                  class="connector-line"
                />
              </svg>

              <div class="scene-area" :style="sceneStyle()">
                <div
                  class="project-circle draggable-node"
                  ref="projectNode"
                  data-drop-zone="project"
                  :style="projectSceneStyle()"
                  @mousedown.stop.prevent="beginDrag('project', hierarchy.project?.id || projectId, $event)"
                >
                  <button
                    type="button"
                    class="icon-btn project-add-btn"
                    title="Create workflow"
                    aria-label="Create workflow"
                    @mousedown.stop
                    @click.stop="openCreateWorkflowModal"
                  >
                    <i class="bi bi-plus-lg"></i>
                  </button>
                  <button
                    type="button"
                    class="project-name entity-name-btn"
                    @mousedown.stop
                    @click.stop="openEntityInfoModal('project', hierarchy.project?.id || projectId, hierarchy.project?.name || projectName)"
                  >
                    {{ hierarchy.project?.name || projectName }}
                  </button>
                  <div v-if="hasUnassigned" class="experiment-circles project-experiments">
                    <div
                      v-for="experiment in hierarchy.unassigned_experiments"
                      :key="`unassigned-${experiment.id}`"
                      class="experiment-circle unassigned-experiment-circle draggable-node"
                      :style="nodeStyle('unassignedExperiment', experiment.id)"
                      :title="experiment.name"
                      @mousedown.stop.prevent="beginDrag('unassignedExperiment', experiment.id, $event, { experimentId: experiment.id, sourceActionId: null })"
                    >
                      <button
                        type="button"
                        class="entity-name-btn experiment-name-btn"
                        @mousedown.stop
                        @click.stop="openEntityInfoModal('experiment', experiment.id, experiment.name, { projectName: hierarchy.project?.name || projectName, actionName: null, workflowName: null })"
                      >
                        {{ truncate(experiment.name, 18) }}
                      </button>
                    </div>
                  </div>
                  <div v-else class="empty-state">No standalone experiments</div>
                </div>

                <div
                  v-for="workflow in hierarchy.workflows"
                  :key="`workflow-${workflow.id}`"
                  class="workflow-circle draggable-node"
                  :class="{ 'drag-source-workflow': isDraggingExperimentInWorkflow(workflow) || isDraggingActionInWorkflow(workflow.id) }"
                  :data-workflow-id="workflow.id"
                  :style="workflowStyle(workflow, workflowIndex(workflow.id))"
                  @mousedown.stop.prevent="beginDrag('workflow', workflow.id, $event)"
                >
                  <button
                    v-if="!isDynamicStructureHierarchy"
                    type="button"
                    class="icon-btn workflow-add-btn"
                    title="Create child element"
                    aria-label="Create child element"
                    @mousedown.stop
                    @click.stop="openCreateActionModal(workflow)"
                  >
                    <i class="bi bi-plus-lg"></i>
                  </button>
                  <button
                    type="button"
                    class="workflow-name entity-name-btn"
                    @mousedown.stop
                    @click.stop="openEntityInfoModal(workflow.__isStructureElement ? 'structure_element' : 'workflow', workflow.id, workflow.name, { projectName: hierarchy.project?.name || projectName, typeName: workflow.type_name })"
                  >
                    {{ workflow.name }}
                  </button>

                  <div v-if="workflow.actions && workflow.actions.length" class="action-list">
                    <div
                      v-for="(action, actionIndex) in workflow.actions"
                      :key="`action-${action.id}`"
                      class="action-circle draggable-node"
                      :class="{ 'drag-source-action': isDraggingExperimentInAction(action.id) || isDraggingNode('action', `${workflow.id}-${action.id}`) }"
                      :data-action-id="action.id"
                      :data-workflow-id="workflow.id"
                      :style="actionStyle(workflow, action)"
                      @mousedown.stop.prevent="onActionMouseDown(workflow, action, $event)"
                    >
                      <button
                        type="button"
                        class="icon-btn action-add-btn"
                        title="Create experiment"
                        aria-label="Create experiment"
                        @mousedown.stop
                        @click.stop="openCreateExperimentModal(action)"
                      >
                        <i class="bi bi-plus-lg"></i>
                      </button>
                      <div
                        v-if="!isDynamicStructureHierarchy"
                        class="action-controls"
                        :class="{ hidden: dragState && dragState.type === 'action' }"
                        @mousedown.stop
                      >
                        <button
                          type="button"
                          class="icon-btn action-arrow"
                          :disabled="isPersistingActionMove || actionIndex === 0"
                          title="Move activity up"
                          @click.stop="moveActionWithinWorkflow(workflow, action, 'up')"
                        >
                          <i class="bi bi-chevron-up"></i>
                        </button>
                        <button
                          type="button"
                          class="icon-btn action-arrow"
                          :disabled="isPersistingActionMove || actionIndex === (workflow.actions.length - 1)"
                          title="Move activity down"
                          @click.stop="moveActionWithinWorkflow(workflow, action, 'down')"
                        >
                          <i class="bi bi-chevron-down"></i>
                        </button>
                      </div>
                      <button
                        type="button"
                        class="action-name entity-name-btn"
                        @mousedown.stop
                        @click.stop="openEntityInfoModal(action.__isStructureElement ? 'structure_element' : 'action', action.__elementId || action.id, action.name, { workflowName: workflow.name, projectName: hierarchy.project?.name || projectName, typeName: action.type_name, path: action.__path })"
                      >
                        {{ action.name }}
                      </button>
                      <div v-if="action.experiments && action.experiments.length" class="experiment-circles action-experiments">
                        <div
                          v-for="experiment in action.experiments"
                          :key="`action-exp-${action.id}-${experiment.id}`"
                          class="experiment-circle tiny action-experiment-circle draggable-node"
                          :class="{ dragging: isDraggingNode('actionExperiment', `${action.id}-${experiment.id}`) }"
                          :style="nodeStyle('actionExperiment', `${action.id}-${experiment.id}`)"
                          :title="experiment.name"
                          @mousedown.stop.prevent="beginDrag('actionExperiment', `${action.id}-${experiment.id}`, $event, { experimentId: experiment.id, sourceActionId: action.id })"
                        >
                          <button
                            type="button"
                            class="entity-name-btn experiment-name-btn"
                            @mousedown.stop
                            @click.stop="openEntityInfoModal('experiment', experiment.id, experiment.name, { actionName: action.name, workflowName: workflow.name, projectName: hierarchy.project?.name || projectName })"
                          >
                            {{ truncate(experiment.name, 14) }}
                          </button>
                        </div>
                      </div>
                      <div v-else class="empty-state">No experiments</div>
                    </div>
                  </div>

                  <div v-else class="empty-state">No activities</div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="!hasUnassigned && !hasWorkflows" class="state">No hierarchy data available.</div>
        </template>
      </div>
    </div>

    <HierarchyCreateWorkflowModal
      v-if="showCreateWorkflowModal"
      :project-name="hierarchy.project?.name || projectName"
      :form="newWorkflow"
      :errors="createWorkflowErrors"
      :is-submitting="isCreatingWorkflow"
      @close="closeCreateWorkflowModal"
      @update-field="updateNewWorkflowField"
      @validate-name="validateNewWorkflowName"
      @form-submit="submitCreateWorkflow"
    />

    <HierarchyCreateActionModal
      v-if="showCreateActionModal"
      :workflow-name="selectedWorkflowForAction?.name || 'Selected workflow'"
      :form="newAction"
      :errors="createActionErrors"
      :locations="locations"
      :is-submitting="isCreatingAction"
      @close="closeCreateActionModal"
      @update-field="updateNewActionField"
      @validate-field="validateNewActionField"
      @form-submit="submitCreateAction"
    />

    <HierarchyCreateExperimentModal
      v-if="showCreateExperimentModal"
      :project-name="projectName"
      :project-id="projectId"
      :action-name="selectedActionForExperiment?.name || ''"
      :form="newExperiment"
      :errors="createExperimentErrors"
      :locations="locations"
      :is-submitting="isCreatingExperiment"
      @close="closeCreateExperimentModal"
      @update-field="updateNewExperimentField"
      @form-submit="submitCreateExperiment"
    />

    <div
      v-if="showEntityInfoModal"
      class="entity-info-overlay"
      role="dialog"
      aria-modal="true"
      :aria-label="entityInfoTitle"
      @click.self="closeEntityInfoModal"
    >
      <div class="entity-info-card">
        <div class="entity-info-header">
          <h3>{{ entityInfoTitle }}</h3>
          <div class="entity-info-header-actions">
            <button
              type="button"
              class="icon-btn entity-nav-btn"
              :aria-label="`Open ${entityInfoTitle} page`"
              @click="navigateToEntityDetail"
            >
              ->
            </button>
            <button type="button" class="icon-btn" :aria-label="`Close ${entityInfoTitle}`" @click="closeEntityInfoModal">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>
        </div>

        <div class="entity-info-body">
          <div v-if="entityInfoLoading" class="state">Loading details...</div>
          <div v-else-if="entityInfoError" class="state error">{{ entityInfoError }}</div>
          <template v-else>
            <div v-if="entityInfoFields.length" class="entity-info-fields">
              <div
                v-for="field in entityInfoFields"
                :key="field.label"
                class="entity-info-field"
              >
                <strong>{{ field.label }}:</strong> {{ field.value || 'N/A' }}
              </div>
            </div>

            <div class="entity-comments-section">
              <h4>Comments</h4>
              <ul v-if="entityComments.length" class="entity-comments-list">
                <li v-for="comment in entityComments" :key="comment.id" class="entity-comment-item">
                  <div class="entity-comment-meta">
                    Created by {{ comment.author_name || 'Unknown' }} at {{ formatEntityInfoDate(comment.timestamp) }}
                  </div>
                  <div class="entity-comment-text">{{ comment.text }}</div>
                </li>
              </ul>
              <div v-else class="empty-state">No comments available.</div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { useToast } from 'vue-toastification';
import apiService from '@/services/apiService';
import { API_ENDPOINTS } from '@/services/config';
import HierarchyCreateWorkflowModal from '@/components/HierarchyCreateWorkflowModal.vue';
import HierarchyCreateActionModal from '@/components/HierarchyCreateActionModal.vue';
import HierarchyCreateExperimentModal from '@/components/HierarchyCreateExperimentModal.vue';

const buildDefaultWorkflow = (projectId = null) => {
  const today = new Date();
  const oneYearFromNow = new Date(today);
  oneYearFromNow.setFullYear(today.getFullYear() + 1);

  return {
    name: '',
    status: 'contract',
    start_date: today.toISOString().split('T')[0],
    end_date: oneYearFromNow.toISOString().split('T')[0],
    project_id: projectId ? Number(projectId) : '',
  };
};

const buildDefaultAction = (workflowId = null) => ({
  name: '',
  description: '',
  status: '',
  location_id: '',
  workflow_id: workflowId ? Number(workflowId) : '',
});

const buildDefaultExperiment = (actionId = null, projectId = null) => ({
  name: '',
  location_id: '',
  start_date: '',
  end_date: '',
  action_id: actionId ? Number(actionId) : '',
  project_id: projectId ? Number(projectId) : '',
});

export default {
  name: 'ProjectHierarchyMap',
  components: {
    HierarchyCreateWorkflowModal,
    HierarchyCreateActionModal,
    HierarchyCreateExperimentModal,
  },
  props: {
    open: {
      type: Boolean,
      default: false,
    },
    projectId: {
      type: Number,
      required: true,
    },
    projectStructureSetId: {
      type: Number,
      default: null,
    },
    projectName: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      hierarchy: {
        project: null,
        workflows: [],
        structure: [],
        unassigned_experiments: [],
      },
      hierarchyMode: 'legacy',
      nodePositions: {},
      connectorLines: [],
      overlaySize: {
        width: 0,
        height: 0,
      },
      zoom: 1,
      panX: 0,
      panY: 0,
      panDragState: null,
      connectorRaf: null,
      dragState: null,
      isPersistingExperimentMove: false,
      isPersistingActionMove: false,
      locations: [],
      showCreateWorkflowModal: false,
      isCreatingWorkflow: false,
      createWorkflowErrors: [],
      newWorkflow: buildDefaultWorkflow(this.projectId),
      showCreateActionModal: false,
      isCreatingAction: false,
      createActionErrors: [],
      selectedWorkflowForAction: null,
      newAction: buildDefaultAction(),
      showCreateExperimentModal: false,
      isCreatingExperiment: false,
      createExperimentErrors: [],
      selectedActionForExperiment: null,
      newExperiment: buildDefaultExperiment(),
      showEntityInfoModal: false,
      entityInfoLoading: false,
      entityInfoError: '',
      entityInfoTitle: 'Entity Details',
      entityInfoFields: [],
      entityComments: [],
      entityInfoEntityType: '',
      entityInfoEntityId: null,
      entityInfoContext: {},
      loading: false,
      error: '',
    };
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  computed: {
    ...mapState(['loggedInUser']),
    isDynamicStructureHierarchy() {
      return this.hierarchyMode === 'dynamic';
    },
    hasWorkflows() {
      return (this.hierarchy.workflows || []).length > 0;
    },
    hasUnassigned() {
      return (this.hierarchy.unassigned_experiments || []).length > 0;
    },
    sceneTransform() {
      return `translate(${this.panX}px, ${this.panY}px) scale(${this.zoom})`;
    },
    sceneRootStyle() {
      return {
        width: `${this.overlaySize.width}px`,
        height: `${this.overlaySize.height}px`,
        transform: this.sceneTransform,
      };
    },
    isInteracting() {
      return Boolean(this.dragState || this.panDragState);
    },
    zoomPercent() {
      return Math.round(this.zoom * 100);
    },
  },
  watch: {
    open: {
      immediate: true,
      handler(isOpen) {
        if (isOpen) {
          this.fetchHierarchy();
          document.body.style.overflow = 'hidden';
          window.addEventListener('keydown', this.onEscape);
          window.addEventListener('resize', this.queueConnectorUpdate);
          window.addEventListener('mousemove', this.handleGlobalMouseMove);
          window.addEventListener('mouseup', this.handleGlobalMouseUp);
          this.$nextTick(() => this.queueConnectorUpdate());
        } else {
          document.body.style.overflow = '';
          this.showCreateWorkflowModal = false;
          this.showCreateActionModal = false;
          this.showCreateExperimentModal = false;
          this.showEntityInfoModal = false;
          window.removeEventListener('keydown', this.onEscape);
          window.removeEventListener('resize', this.queueConnectorUpdate);
          window.removeEventListener('mousemove', this.handleGlobalMouseMove);
          window.removeEventListener('mouseup', this.handleGlobalMouseUp);
          this.connectorLines = [];
        }
      },
    },
    projectId() {
      if (this.open) {
        this.fetchHierarchy();
      }
      this.newWorkflow.project_id = this.projectId ? Number(this.projectId) : '';
    },
    projectStructureSetId() {
      if (this.open) {
        this.fetchHierarchy();
      }
    },
  },
  beforeUnmount() {
    this.detachDragListeners();
    if (this.connectorRaf) {
      cancelAnimationFrame(this.connectorRaf);
      this.connectorRaf = null;
    }
    document.body.style.overflow = '';
    window.removeEventListener('keydown', this.onEscape);
    window.removeEventListener('resize', this.queueConnectorUpdate);
    window.removeEventListener('mousemove', this.handleGlobalMouseMove);
    window.removeEventListener('mouseup', this.handleGlobalMouseUp);
  },
  methods: {
    normalizeHierarchyResponse(response, preferredLastWorkflowId = null) {
      const hasDynamicStructure = Array.isArray(response?.structure);

      if (!hasDynamicStructure) {
        const workflows = this.moveWorkflowToEnd(response?.workflows || [], preferredLastWorkflowId);
        return {
          mode: 'legacy',
          hierarchy: {
            project: response?.project || null,
            workflows,
            structure: [],
            unassigned_experiments: response?.unassigned_experiments || [],
          },
        };
      }

      const structure = response?.structure || [];
      const workflows = this.structureRootsToWorkflowRows(structure, preferredLastWorkflowId);

      return {
        mode: 'dynamic',
        hierarchy: {
          project: response?.project || null,
          workflows,
          structure,
          unassigned_experiments: response?.unassigned_experiments || [],
        },
      };
    },
    structureRootsToWorkflowRows(structureRoots, preferredLastWorkflowId = null) {
      const rows = (Array.isArray(structureRoots) ? structureRoots : []).map((rootNode) => {
        const flattenedChildren = this.flattenStructureNodesToActionRows(rootNode?.children || [], 0);
        const selfActionRow = (rootNode?.can_have_experiments && Array.isArray(rootNode?.experiments) && rootNode.experiments.length)
          ? [{
              id: Number(`${rootNode.id}0000`),
              name: rootNode.name,
              type_name: rootNode.type_name || 'Element',
              experiments: rootNode.experiments || [],
              __depth: 0,
              __isStructureElement: true,
              __elementId: rootNode.id,
              __path: rootNode.path || rootNode.name,
            }]
          : [];

        return {
          id: rootNode.id,
          name: rootNode.name,
          type_id: rootNode.type_id,
          type_name: rootNode.type_name || 'Element',
          actions: [...selfActionRow, ...flattenedChildren],
          __isStructureElement: true,
          __elementId: rootNode.id,
          __path: rootNode.path || rootNode.name,
        };
      });

      return this.moveWorkflowToEnd(rows, preferredLastWorkflowId);
    },
    flattenStructureNodesToActionRows(nodes, depth = 0) {
      const rows = [];
      (Array.isArray(nodes) ? nodes : []).forEach((node) => {
        const indent = depth > 0 ? `${'↳ '.repeat(depth)}` : '';
        rows.push({
          id: node.id,
          name: `${indent}${node.name}`,
          type_name: node.type_name || 'Element',
          experiments: Array.isArray(node.experiments) ? node.experiments : [],
          __depth: depth,
          __isStructureElement: true,
          __elementId: node.id,
          __path: node.path || node.name,
        });

        if (Array.isArray(node.children) && node.children.length) {
          rows.push(...this.flattenStructureNodesToActionRows(node.children, depth + 1));
        }
      });
      return rows;
    },
    updateNewWorkflowField(field, value) {
      this.newWorkflow = {
        ...this.newWorkflow,
        [field]: value,
      };
    },
    updateNewActionField(field, value) {
      this.newAction = {
        ...this.newAction,
        [field]: value,
      };
    },
    updateNewExperimentField(field, value) {
      this.newExperiment = {
        ...this.newExperiment,
        [field]: value,
      };
    },
    moveWorkflowToEnd(workflows, workflowId) {
      const normalizedId = Number(workflowId);
      if (!Number.isFinite(normalizedId)) return Array.isArray(workflows) ? workflows : [];

      const nextWorkflows = Array.isArray(workflows) ? [...workflows] : [];
      const workflowIndex = nextWorkflows.findIndex((workflow) => Number(workflow.id) === normalizedId);
      if (workflowIndex === -1) return nextWorkflows;

      const [workflow] = nextWorkflows.splice(workflowIndex, 1);
      nextWorkflows.push(workflow);
      return nextWorkflows;
    },
    async fetchHierarchy(preferredLastWorkflowId = null) {
      this.loading = true;
      this.error = '';
      try {
        const response = await apiService.getProjectHierarchy(
          this.projectId,
          this.loggedInUser?.entra_id,
          this.projectStructureSetId
        );
        const normalized = this.normalizeHierarchyResponse(response, preferredLastWorkflowId);
        this.hierarchyMode = normalized.mode;
        this.hierarchy = normalized.hierarchy;
      } catch (error) {
        console.error('Failed to load project hierarchy:', error);
        this.error = 'Failed to load project hierarchy.';
      } finally {
        this.loading = false;
        this.$nextTick(() => {
          this.resetView();
          this.queueConnectorUpdate();
        });
      }
    },
    formatEntityInfoDate(dateString) {
      if (!dateString) return 'N/A';
      const options = {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short',
      };
      return new Date(dateString).toLocaleDateString(undefined, options);
    },
    entityDuration(startDate, endDate) {
      if (!startDate || !endDate) return 'N/A';

      const start = new Date(startDate);
      const end = new Date(endDate);
      const durationMs = end - start;

      if (Number.isNaN(durationMs) || durationMs < 0) return 'N/A';

      const seconds = Math.floor((durationMs / 1000) % 60);
      const minutes = Math.floor((durationMs / (1000 * 60)) % 60);
      const hours = Math.floor(durationMs / (1000 * 60 * 60));

      return `${hours} hour(s), ${minutes} minute(s), ${seconds} second(s)`;
    },
    async enrichCommentsWithUserNames(comments) {
      if (!Array.isArray(comments) || comments.length === 0) {
        return comments;
      }

      const enriched = await Promise.all(
        comments.map(async (comment) => {
          if (!comment.author_id) {
            return { ...comment, author_name: 'Unknown' };
          }

          try {
            const user = await apiService.get(
              API_ENDPOINTS.user(comment.author_id),
              {},
              this.loggedInUser?.entra_id
            );
            return {
              ...comment,
              author_name: user?.name || comment.author_id,
            };
          } catch (error) {
            // Fallback to author_id if lookup fails
            return {
              ...comment,
              author_name: comment.author_id,
            };
          }
        })
      );

      return enriched;
    },
    async fetchEntityInfoModalData(entityType, entityId, context = {}) {
      this.entityInfoLoading = true;
      this.entityInfoError = '';

      try {
        const endpointMap = {
          project: API_ENDPOINTS.project,
          workflow: API_ENDPOINTS.workflow,
          action: API_ENDPOINTS.action,
          experiment: API_ENDPOINTS.experiment,
          structure_element: API_ENDPOINTS.projectStructureElement,
        };
        const endpointFactory = endpointMap[entityType];
        if (!endpointFactory) {
          throw new Error(`Unsupported entity type: ${entityType}`);
        }

        const [entityResponse, commentsResponse] = await Promise.all([
          apiService.get(endpointFactory(entityId), {}, this.loggedInUser?.entra_id),
          entityType === 'structure_element'
            ? apiService.get(
              API_ENDPOINTS.projectStructureElementComments(entityId),
              {},
              this.loggedInUser?.entra_id
            )
            : apiService.get(
              API_ENDPOINTS.associativeComments,
              {
                comment_type: entityType,
                entity_id: entityId,
              },
              this.loggedInUser?.entra_id
            ),
        ]);

        const entity = entityResponse || null;
        const contextNames = {
          ...context,
          locationName: 'N/A',
          userName: 'N/A',
          projectName: context.projectName || this.hierarchy.project?.name || this.projectName || 'N/A',
        };

        if (entityType === 'action' && entity?.location_id) {
          try {
            const location = await apiService.get(API_ENDPOINTS.location(entity.location_id), {}, this.loggedInUser?.entra_id);
            contextNames.locationName = location?.name || entity.location_id;
          } catch (error) {
            contextNames.locationName = entity.location_id;
          }
        }

        if (entityType === 'experiment') {
          if (entity?.location_id) {
            try {
              const location = await apiService.get(API_ENDPOINTS.location(entity.location_id), {}, this.loggedInUser?.entra_id);
              contextNames.locationName = location?.name || entity.location_id;
            } catch (error) {
              contextNames.locationName = entity.location_id;
            }
          }
          if (entity?.user_id) {
            try {
              const user = await apiService.get(API_ENDPOINTS.user(entity.user_id), {}, this.loggedInUser?.entra_id);
              contextNames.userName = user?.name || entity.user_id;
            } catch (error) {
              contextNames.userName = entity.user_id;
            }
          }
        }

        this.entityInfoFields = this.buildEntityInfoFields(entityType, entity, contextNames);
        const rawComments = Array.isArray(commentsResponse) ? commentsResponse : [];
        this.entityComments = await this.enrichCommentsWithUserNames(rawComments);
      } catch (error) {
        console.error('Failed to fetch entity details/comments for hierarchy map:', error);
        this.entityInfoError = 'Failed to load details.';
      } finally {
        this.entityInfoLoading = false;
      }
    },
    buildEntityInfoFields(entityType, entity, context = {}) {
      const safeEntity = entity || {};
      if (entityType === 'project') {
        return [
          { label: 'Name', value: safeEntity.name || this.projectName || 'N/A' },
          { label: 'Project Number', value: safeEntity.project_number || 'N/A' },
          { label: 'Start Date', value: this.formatEntityInfoDate(safeEntity.start_date) },
          { label: 'End Date', value: this.formatEntityInfoDate(safeEntity.end_date) },
          { label: 'Duration', value: this.entityDuration(safeEntity.start_date, safeEntity.end_date) },
        ];
      }
      if (entityType === 'workflow') {
        return [
          { label: 'Name', value: safeEntity.name || 'N/A' },
          { label: 'Status', value: safeEntity.status || 'N/A' },
          { label: 'Start Date', value: this.formatEntityInfoDate(safeEntity.start_date) },
          { label: 'End Date', value: this.formatEntityInfoDate(safeEntity.end_date) },
          { label: 'Project', value: context.projectName || safeEntity.project_id || 'N/A' },
        ];
      }
      if (entityType === 'action') {
        return [
          { label: 'Name', value: safeEntity.name || 'N/A' },
          { label: 'Description', value: safeEntity.description || 'N/A' },
          { label: 'Status', value: safeEntity.status || 'N/A' },
          { label: 'Project', value: context.projectName || 'N/A' },
          { label: 'Workflow', value: context.workflowName || safeEntity.workflow_id || 'N/A' },
          { label: 'Location', value: context.locationName || safeEntity.location_id || 'N/A' },
        ];
      }
      if (entityType === 'structure_element') {
        return [
          { label: 'Name', value: safeEntity.name || 'N/A' },
          { label: 'Type', value: context.typeName || safeEntity.type_name || 'N/A' },
          { label: 'Path', value: context.path || safeEntity.path || 'N/A' },
          { label: 'Status', value: safeEntity.status || 'N/A' },
          { label: 'Project', value: context.projectName || safeEntity.project_id || 'N/A' },
        ];
      }
      return [
        { label: 'Name', value: safeEntity.name || 'N/A' },
        { label: 'Start Date', value: this.formatEntityInfoDate(safeEntity.start_date) },
        { label: 'End Date', value: this.formatEntityInfoDate(safeEntity.end_date) },
        { label: 'Duration', value: this.entityDuration(safeEntity.start_date, safeEntity.end_date) },
        { label: 'Project', value: context.projectName || safeEntity.project_id || 'N/A' },
        { label: 'Workflow', value: context.workflowName || 'N/A' },
        { label: 'Activity', value: context.actionName || safeEntity.action_id || 'N/A' },
        { label: 'Location', value: context.locationName || safeEntity.location_id || 'N/A' },
        { label: 'User', value: context.userName || safeEntity.user_id || 'N/A' },
      ];
    },
    async openEntityInfoModal(entityType, entityId, entityName = '', context = {}) {
      if (this.entityInfoLoading || !entityId) return;
      const typeLabelMap = {
        project: 'Project',
        workflow: 'Workflow',
        action: 'Activity',
        experiment: 'Experiment',
        structure_element: context.typeName || 'Structure Element',
      };
      const label = typeLabelMap[entityType] || 'Entity';
      this.entityInfoTitle = `${label} Details${entityName ? ` - ${entityName}` : ''}`;
      this.entityInfoEntityType = entityType;
      this.entityInfoEntityId = Number(entityId);
      this.entityInfoContext = { ...context };
      this.showEntityInfoModal = true;
      await this.fetchEntityInfoModalData(entityType, entityId, context);
    },
    async navigateToEntityDetail() {
      if (!this.entityInfoEntityType || !this.entityInfoEntityId) return;

      const routeNameByType = {
        project: 'ProjectDetailPage',
        workflow: 'WorkflowDetailPage',
        action: 'ActionDetailPage',
        experiment: 'ExperimentDetailPage',
        structure_element: 'StructureElementDetailPage',
      };
      const routeName = routeNameByType[this.entityInfoEntityType];
      if (!routeName) return;

      const route = {
        name: routeName,
        params: {
          id: this.entityInfoEntityId,
        },
      };

      if (this.entityInfoEntityType === 'structure_element') {
        route.params = {
          projectId: this.projectId,
          elementId: this.entityInfoEntityId,
        };
      }

      if (this.entityInfoEntityType === 'action') {
        route.query = {
          workflowName: this.entityInfoContext.workflowName || '',
          projectID: String(this.projectId),
          projectName: this.entityInfoContext.projectName || this.hierarchy.project?.name || this.projectName || '',
        };
      }

      const isSameRoute = this.entityInfoEntityType === 'structure_element'
        ? (
          this.$route?.name === route.name
          && Number(this.$route?.params?.projectId) === Number(route.params.projectId)
          && Number(this.$route?.params?.elementId) === Number(route.params.elementId)
        )
        : (
          this.$route?.name === route.name
          && Number(this.$route?.params?.id) === Number(route.params.id)
        );

      this.closeEntityInfoModal();
      this.$emit('close');

      if (isSameRoute) return;

      try {
        await this.$router.push(route);
      } catch (error) {
        console.error('Failed to navigate to entity detail page:', error);
      }
    },
    closeEntityInfoModal() {
      if (this.entityInfoLoading) return;
      this.showEntityInfoModal = false;
      this.entityInfoError = '';
      this.entityInfoEntityType = '';
      this.entityInfoEntityId = null;
      this.entityInfoContext = {};
    },
    resetNewWorkflowForm() {
      this.newWorkflow = buildDefaultWorkflow(this.projectId);
      this.createWorkflowErrors = [];
    },
    openCreateWorkflowModal() {
      if (this.isCreatingWorkflow) return;
      this.resetNewWorkflowForm();
      this.showCreateWorkflowModal = true;
    },
    closeCreateWorkflowModal() {
      if (this.isCreatingWorkflow) return;
      this.showCreateWorkflowModal = false;
      this.createWorkflowErrors = [];
    },
    resetNewActionForm(workflow = null) {
      this.newAction = buildDefaultAction(workflow?.id || null);
      this.createActionErrors = [];
      this.selectedWorkflowForAction = workflow || null;
    },
    async fetchLocations() {
      try {
        this.locations = await apiService.fetchResources(API_ENDPOINTS.locations, this.loggedInUser?.entra_id);
      } catch (error) {
        console.error('Failed to fetch locations for hierarchy map:', error);
        this.toast.error('Failed to fetch locations.');
      }
    },
    async openCreateActionModal(workflow) {
      if (this.isCreatingAction || !workflow) return;
      if (!this.locations.length) {
        await this.fetchLocations();
      }
      this.resetNewActionForm(workflow);
      this.showCreateActionModal = true;
    },
    closeCreateActionModal() {
      if (this.isCreatingAction) return;
      this.showCreateActionModal = false;
      this.createActionErrors = [];
    },
    validateNewWorkflowName() {
      const normalizedName = this.newWorkflow.name?.trim() || '';
      this.newWorkflow = {
        ...this.newWorkflow,
        name: normalizedName,
      };

      if (!normalizedName) {
        if (!this.createWorkflowErrors.includes('name')) {
          this.createWorkflowErrors.push('name');
        }
      } else {
        this.createWorkflowErrors = this.createWorkflowErrors.filter((item) => item !== 'name');
      }
    },
    async submitCreateWorkflow() {
      if (this.isCreatingWorkflow) return;
      this.validateNewWorkflowName();
      if (this.createWorkflowErrors.length) {
        this.toast.error('Please fill out the workflow name.');
        return;
      }

      this.isCreatingWorkflow = true;

      try {
        const response = await apiService.createResource(
          API_ENDPOINTS.workflows,
          {
            ...this.newWorkflow,
            name: this.newWorkflow.name.trim(),
            project_id: Number(this.projectId),
          },
          this.loggedInUser?.entra_id
        );

        this.toast.success('Workflow created successfully!');
        this.showCreateWorkflowModal = false;
        await this.fetchHierarchy(response?.id);
      } catch (error) {
        console.error('Failed to create workflow from hierarchy map:', error);
        this.toast.error('Failed to create workflow.');
      } finally {
        this.isCreatingWorkflow = false;
      }
    },
    validateNewActionField(field, value) {
      const normalizedValue = typeof value === 'string' ? value.trim() : value;
      if (!normalizedValue) {
        if (!this.createActionErrors.includes(field)) {
          this.createActionErrors.push(field);
        }
      } else {
        this.createActionErrors = this.createActionErrors.filter((item) => item !== field);
      }
    },
    validateNewAction() {
      ['name', 'description', 'status', 'location_id'].forEach((field) => {
        this.validateNewActionField(field, this.newAction[field]);
      });
      return this.createActionErrors.length === 0;
    },
    openCreateExperimentModal(action) {
      if (this.isCreatingExperiment || !action) return;
      if (this.locations.length === 0) this.fetchLocations();
      this.selectedActionForExperiment = action;
      this.newExperiment = buildDefaultExperiment(action.id, this.projectId);
      if (this.isDynamicStructureHierarchy) {
        this.newExperiment = {
          ...this.newExperiment,
          project_structure_element_id: Number(action.__elementId || action.id),
          action_id: '',
        };
      }
      this.createExperimentErrors = [];
      this.showCreateExperimentModal = true;
    },
    closeCreateExperimentModal() {
      if (this.isCreatingExperiment) return;
      this.showCreateExperimentModal = false;
      this.selectedActionForExperiment = null;
    },
    validateNewExperiment() {
      this.createExperimentErrors = [];
      if (!this.newExperiment.name?.trim()) this.createExperimentErrors.push('name');
      if (!this.newExperiment.location_id) this.createExperimentErrors.push('location_id');
      return this.createExperimentErrors.length === 0;
    },
    async submitCreateExperiment() {
      if (this.isCreatingExperiment) return;
      if (!this.selectedActionForExperiment?.id) return;
      if (!this.validateNewExperiment()) {
        this.toast.error('Please fill out all required experiment fields.');
        return;
      }
      this.isCreatingExperiment = true;
      try {
        const selectedElementId = Number(this.selectedActionForExperiment.__elementId || this.selectedActionForExperiment.id);
        const experimentPayload = this.isDynamicStructureHierarchy
          ? {
              name: this.newExperiment.name.trim(),
              location_id: Number(this.newExperiment.location_id),
              project_structure_element_id: selectedElementId,
              project_id: Number(this.projectId),
              user_id: this.loggedInUser.id,
              ...(this.newExperiment.start_date ? { start_date: this.newExperiment.start_date } : {}),
              ...(this.newExperiment.end_date ? { end_date: this.newExperiment.end_date } : {}),
            }
          : {
              name: this.newExperiment.name.trim(),
              location_id: Number(this.newExperiment.location_id),
              action_id: Number(this.selectedActionForExperiment.id),
              project_id: Number(this.projectId),
              ...(this.newExperiment.start_date ? { start_date: this.newExperiment.start_date } : {}),
              ...(this.newExperiment.end_date ? { end_date: this.newExperiment.end_date } : {}),
            };

        await apiService.createResource(
          API_ENDPOINTS.experiments,
          experimentPayload,
          this.loggedInUser?.entra_id
        );
        this.toast.success('Experiment created successfully!');
        this.showCreateExperimentModal = false;
        await this.fetchHierarchy();
      } catch (error) {
        console.error('Failed to create experiment from hierarchy map:', error);
        this.toast.error('Failed to create experiment.');
      } finally {
        this.isCreatingExperiment = false;
      }
    },
    async submitCreateAction() {
      if (this.isDynamicStructureHierarchy) {
        this.toast.error('Create child elements from the structure element page.');
        return;
      }
      if (this.isCreatingAction) return;
      if (!this.selectedWorkflowForAction?.id) return;
      if (!this.validateNewAction()) {
        this.toast.error('Please fill out all required activity fields.');
        return;
      }

      this.isCreatingAction = true;

      try {
        await apiService.createResource(
          API_ENDPOINTS.workflowActions(this.selectedWorkflowForAction.id),
          {
            name: this.newAction.name.trim(),
            description: this.newAction.description.trim(),
            status: this.newAction.status,
            location_id: Number(this.newAction.location_id),
          },
          this.loggedInUser?.entra_id
        );

        this.toast.success('Activity created successfully!');
        this.showCreateActionModal = false;
        await this.fetchHierarchy();
      } catch (error) {
        console.error('Failed to create activity from hierarchy map:', error);
        this.toast.error('Failed to create activity.');
      } finally {
        this.isCreatingAction = false;
      }
    },
    clamp(value, min, max) {
      return Math.min(Math.max(value, min), max);
    },
    truncate(value, maxLength) {
      if (!value) return '';
      if (value.length <= maxLength) return value;
      return `${value.slice(0, maxLength - 1)}…`;
    },
    nodeKey(type, id) {
      return `${type}:${id}`;
    },
    isDraggingNode(type, id) {
      return this.dragState?.key === this.nodeKey(type, id);
    },
    isDraggingExperimentInAction(actionId) {
      const key = this.dragState?.key || '';
      return this.dragState?.type === 'actionExperiment' && key.startsWith(`actionExperiment:${actionId}-`);
    },
    isDraggingExperimentInWorkflow(workflow) {
      if (this.dragState?.type !== 'actionExperiment') return false;
      return (workflow?.actions || []).some((action) => this.isDraggingExperimentInAction(action.id));
    },
    isDraggingActionInWorkflow(workflowId) {
      return this.dragState?.type === 'action' && Number(this.dragState?.meta?.workflowId) === Number(workflowId);
    },
    nodeStyle(type, id) {
      const key = this.nodeKey(type, id);
      const pos = this.nodePositions[key];
      if (!pos) {
        if (this.dragState?.key === key) {
          return { zIndex: 120 };
        }
        return null;
      }
      return {
        transform: `translate(${pos.x}px, ${pos.y}px)`,
        ...(this.dragState?.key === key ? { zIndex: 120 } : {}),
      };
    },
    mergeNodeStyle(type, id, style = {}) {
      return {
        ...(style || {}),
        ...(this.nodeStyle(type, id) || {}),
      };
    },
    workflowIndex(workflowId) {
      return (this.hierarchy.workflows || []).findIndex((workflow) => workflow.id === workflowId);
    },
    sceneStyle() {
      const sceneSize = this.sceneDimension();

      return {
        width: `${sceneSize}px`,
        minHeight: `${sceneSize}px`,
      };
    },
    getWorkflowDimensions(workflow) {
      const actions = workflow?.actions || [];
      const actionDims = actions.map((action) => this.getActionDimensions(action));
      const maxActionWidth = actionDims.length ? Math.max(...actionDims.map((dimension) => dimension.width)) : 250;
      const totalActionHeight = actionDims.reduce((sum, dimension) => sum + dimension.height, 0);
      const actionGap = Math.max(actionDims.length - 1, 0) * 10;

      return {
        width: Math.max(320, Math.min(600, maxActionWidth + 60)),
        height: Math.max(210, Math.min(1000, 96 + totalActionHeight + actionGap)),
      };
    },
    sceneDimension() {
      const marginX = 48;
      const gapX = 24;
      const workflowCount = this.hierarchy?.workflows?.length || 0;
      const projectExperimentCount = this.hierarchy?.unassigned_experiments?.length || 0;
      const actionCount = (this.hierarchy?.workflows || []).reduce((sum, workflow) => sum + (workflow?.actions?.length || 0), 0);
      const workflowWidths = (this.hierarchy?.workflows || []).map((workflow) => this.getWorkflowDimensions(workflow).width);
      const totalWorkflowRowWidth = workflowWidths.length
        ? workflowWidths.reduce((sum, width) => sum + width, 0) + (Math.max(workflowWidths.length - 1, 0) * gapX) + (marginX * 2)
        : 0;

      return Math.max(
        1000,
        760 + (workflowCount * 90) + (projectExperimentCount * 20) + (actionCount * 28),
        totalWorkflowRowWidth
      );
    },
    projectStyle() {
      const experimentCount = this.hierarchy?.unassigned_experiments?.length || 0;
      const perRow = 3;
      const rows = experimentCount > 0 ? Math.ceil(experimentCount / perRow) : 0;
      const width = 404;
      const contentHeight = rows > 0 ? (rows * 92) + ((rows - 1) * 12) : 0;
      const height = Math.max(190, Math.min(760, 96 + contentHeight + (experimentCount > 0 ? 24 : 0)));

      return {
        width: `${width}px`,
        minHeight: `${height}px`,
        height: `${height}px`,
      };
    },
    projectSceneMetrics() {
      const base = this.projectStyle();
      const width = parseFloat(base.width);
      const height = parseFloat(base.height || base.minHeight);
      const sceneSize = this.sceneDimension();
      const dragOffset = this.nodePositions[this.nodeKey('project', this.hierarchy.project?.id || this.projectId)] || { x: 0, y: 0 };

      return {
        width,
        height,
        left: (sceneSize / 2) - (width / 2) + dragOffset.x,
        top: 38 + dragOffset.y,
      };
    },
    projectSceneStyle() {
      const base = this.projectStyle();
      const metrics = this.projectSceneMetrics();

      return {
        ...base,
        position: 'absolute',
        left: `${metrics.left}px`,
        top: `${metrics.top}px`,
        transform: undefined,
      };
    },
    workflowMetrics(workflow, index) {
      const workflowDimensions = this.getWorkflowDimensions(workflow);
      const width = workflowDimensions.width;
      const height = workflowDimensions.height;
      const sceneSize = this.sceneDimension();
      const workflows = this.hierarchy?.workflows || [];
      const projectMetrics = this.projectSceneMetrics();

      const baseSizes = workflows.map((item) => this.getWorkflowDimensions(item));

      const marginX = 48;
      const gapX = 24;
      const gapY = 34;
      const usableWidth = sceneSize - (marginX * 2);

      const rows = [];
      let activeRow = [];
      let rowWidth = 0;

      baseSizes.forEach((size, itemIndex) => {
        const proposedWidth = activeRow.length ? rowWidth + gapX + size.width : size.width;
        if (activeRow.length && proposedWidth > usableWidth) {
          rows.push(activeRow);
          activeRow = [];
          rowWidth = 0;
        }

        activeRow.push({ index: itemIndex, ...size });
        rowWidth = activeRow.length === 1 ? size.width : rowWidth + gapX + size.width;
      });

      if (activeRow.length) {
        rows.push(activeRow);
      }

      const layout = {};
      let cursorY = projectMetrics.top + projectMetrics.height + 90;

      rows.forEach((row) => {
        const currentRowWidth = row.reduce((sum, item) => sum + item.width, 0) + (Math.max(row.length - 1, 0) * gapX);
        const rowHeight = row.reduce((max, item) => Math.max(max, item.height), 0);
        let cursorX = (sceneSize - currentRowWidth) / 2;

        row.forEach((item) => {
          layout[item.index] = {
            width: item.width,
            height: item.height,
            left: cursorX,
            top: cursorY,
          };
          cursorX += item.width + gapX;
        });

        cursorY += rowHeight + gapY;
      });

      const baseLayout = layout[index] || {
        width,
        height,
        left: (sceneSize - width) / 2,
        top: projectMetrics.top + projectMetrics.diameter + 90,
      };

      const dragOffset = this.nodePositions[this.nodeKey('workflow', workflow.id)] || { x: 0, y: 0 };

      return {
        width: baseLayout.width,
        height: baseLayout.height,
        left: baseLayout.left + dragOffset.x,
        top: baseLayout.top + dragOffset.y,
      };
    },
    workflowStyle(workflow, index) {
      const metrics = this.workflowMetrics(workflow, index);

      return {
        position: 'absolute',
        left: `${metrics.left}px`,
        top: `${metrics.top}px`,
        width: `${metrics.width}px`,
        minHeight: `${metrics.height}px`,
        height: 'auto',
      };
    },
    getActionDimensions(action) {
      const experimentCount = action?.experiments?.length || 0;
      const experimentsPerRow = 3;
      const rows = experimentCount > 0 ? Math.ceil(experimentCount / experimentsPerRow) : 0;
      const width = 300;
      const experimentRowsHeight = rows > 0 ? (rows * 72) + ((rows - 1) * 12) : 0;
      const height = Math.max(170, Math.min(680, 84 + experimentRowsHeight));

      return { width, height };
    },
    actionStyle(workflow, action) {
      const dimensions = this.getActionDimensions(action);

      return this.mergeNodeStyle('action', `${workflow.id}-${action.id}`, {
        width: `${dimensions.width}px`,
        minHeight: `${dimensions.height}px`,
        height: `${dimensions.height}px`,
      });
    },
    beginDrag(type, id, event, meta = null) {
      if (event.button !== 0) return;
      if (this.isPersistingExperimentMove || this.isPersistingActionMove) return;

      let resolvedMeta = meta;
      if (!resolvedMeta && type === 'action') {
        const [workflowIdRaw, actionIdRaw] = String(id).split('-');
        const sourceLocation = this.findActionLocation(Number(actionIdRaw), this.hierarchy);
        resolvedMeta = {
          workflowId: Number(workflowIdRaw),
          actionId: Number(actionIdRaw),
          sourceIndex: sourceLocation ? sourceLocation.actionIndex : null,
          originalHierarchy: JSON.parse(JSON.stringify(this.hierarchy)),
        };
      }

      const key = this.nodeKey(type, id);
      const current = this.nodePositions[key] || { x: 0, y: 0 };
      const nodeRect = event.currentTarget?.getBoundingClientRect?.();
      const bounds = this.getDragBounds(type, event.currentTarget, current);

      this.dragState = {
        key,
        startClientX: event.clientX,
        startClientY: event.clientY,
        startX: current.x,
        startY: current.y,
        nodeRect,
        bounds,
        type,
        id,
        meta: resolvedMeta,
      };
    },
    onDrag(event) {
      if (!this.dragState) return;

      if (this.dragState.type === 'action') {
        const dx = event.clientX - this.dragState.startClientX;
        const dy = event.clientY - this.dragState.startClientY;
        this.nodePositions = {
          ...this.nodePositions,
          [this.dragState.key]: {
            x: this.dragState.startX + (dx / this.zoom),
            y: this.dragState.startY + (dy / this.zoom),
          },
        };

        return;
      }

      const dx = event.clientX - this.dragState.startClientX;
      const dy = event.clientY - this.dragState.startClientY;
      let nextX = this.dragState.startX + (dx / this.zoom);
      let nextY = this.dragState.startY + (dy / this.zoom);

      if (this.dragState.bounds) {
        nextX = Math.min(this.dragState.bounds.maxX, Math.max(this.dragState.bounds.minX, nextX));
        nextY = Math.min(this.dragState.bounds.maxY, Math.max(this.dragState.bounds.minY, nextY));
      }

      this.nodePositions = {
        ...this.nodePositions,
        [this.dragState.key]: {
          x: nextX,
          y: nextY,
        },
      };

      this.queueConnectorUpdate();
    },
    endDrag() {
      this.dragState = null;
    },
    attachDragListeners() {
    },
    detachDragListeners() {
    },
    getDragBounds(type, element, currentPosition) {
      if (!element) return null;

      if (type === 'unassignedExperiment' || type === 'actionExperiment') {
        return null;
      }

      let container = null;
      const inset = 10;

      if (type === 'unassignedExperiment') {
        container = element.closest('.project-circle');
      } else if (type === 'actionExperiment') {
        container = element.closest('.action-circle');
      }

      if (!container) {
        return null;
      }

      const containerRect = container.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();

      return {
        minX: currentPosition.x + (containerRect.left + inset - elementRect.left),
        maxX: currentPosition.x + (containerRect.right - inset - elementRect.right),
        minY: currentPosition.y + (containerRect.top + inset - elementRect.top),
        maxY: currentPosition.y + (containerRect.bottom - inset - elementRect.bottom),
      };
    },
    findActionById(actionId, hierarchy = this.hierarchy) {
      const workflows = hierarchy?.workflows || [];
      for (const workflow of workflows) {
        const action = (workflow.actions || []).find((item) => Number(item.id) === Number(actionId));
        if (action) return action;
      }
      return null;
    },
    findActionLocation(actionId, hierarchy = this.hierarchy) {
      const workflows = hierarchy?.workflows || [];
      for (let workflowIndex = 0; workflowIndex < workflows.length; workflowIndex += 1) {
        const workflow = workflows[workflowIndex];
        const actionIndex = (workflow.actions || []).findIndex((item) => Number(item.id) === Number(actionId));
        if (actionIndex !== -1) {
          return {
            workflowId: Number(workflow.id),
            workflowIndex,
            actionIndex,
          };
        }
      }
      return null;
    },
    pointInsideRect(clientX, clientY, rect) {
      if (!rect) return false;
      return (
        clientX >= rect.left
        && clientX <= rect.right
        && clientY >= rect.top
        && clientY <= rect.bottom
      );
    },
    detectExperimentDropTarget(event, dragState) {
      if (!event) return null;

      const clientX = event.clientX;
      const clientY = event.clientY;
      const sourceActionId = dragState?.meta?.sourceActionId;

      const actionElements = Array.from(document.querySelectorAll('.action-circle[data-action-id]'));
      for (const actionElement of actionElements) {
        const actionId = Number(actionElement.dataset.actionId);
        if (Number.isFinite(actionId) && Number(actionId) === Number(sourceActionId)) {
          continue;
        }

        if (this.pointInsideRect(clientX, clientY, actionElement.getBoundingClientRect())) {
          return {
            type: 'action',
            actionId,
          };
        }
      }

      const projectElement = this.$refs.projectNode;
      if (projectElement && this.pointInsideRect(clientX, clientY, projectElement.getBoundingClientRect())) {
        return {
          type: 'standalone',
          actionId: null,
        };
      }

      return null;
    },
    restoreDragPosition(state) {
      if (!state) return;
      this.nodePositions = {
        ...this.nodePositions,
        [state.key]: {
          x: state.startX,
          y: state.startY,
        },
      };
    },
    clearExperimentNodePositions(experimentId) {
      const nextPositions = { ...this.nodePositions };
      const experimentSuffix = `-${experimentId}`;
      const unassignedKey = `unassignedExperiment:${experimentId}`;

      Object.keys(nextPositions).forEach((key) => {
        if (key === unassignedKey || key.endsWith(experimentSuffix)) {
          delete nextPositions[key];
        }
      });

      this.nodePositions = nextPositions;
    },
    clearActionNodePosition(actionId) {
      const nextPositions = { ...this.nodePositions };
      Object.keys(nextPositions).forEach((key) => {
        if (key.startsWith('action:') && key.endsWith(`-${actionId}`)) {
          delete nextPositions[key];
        }
      });
      this.nodePositions = nextPositions;
    },
    resetActionDragPosition(state) {
      const actionId = Number(state?.meta?.actionId);
      if (!Number.isFinite(actionId)) {
        this.restoreDragPosition(state);
        return;
      }
      this.clearActionNodePosition(actionId);
    },
    detectActionDropTarget(event) {
      if (!event) return null;
      const clientX = event.clientX;
      const clientY = event.clientY;

      const workflowElements = Array.from(document.querySelectorAll('.workflow-circle[data-workflow-id]'));
      for (const workflowElement of workflowElements) {
        const targetWorkflowId = Number(workflowElement.dataset.workflowId);
        if (!Number.isFinite(targetWorkflowId)) continue;
        if (this.pointInsideRect(clientX, clientY, workflowElement.getBoundingClientRect())) {
          return {
            type: 'workflow',
            workflowId: targetWorkflowId,
          };
        }
      }

      return null;
    },
    buildActionMovePreview(hierarchyInput, actionId, target) {
      if (!target) return null;

      const hierarchyClone = JSON.parse(JSON.stringify(hierarchyInput));
      const sourceLocation = this.findActionLocation(actionId, hierarchyClone);
      if (!sourceLocation) return null;

      const sourceWorkflow = hierarchyClone.workflows[sourceLocation.workflowIndex];
      const [draggedAction] = sourceWorkflow.actions.splice(sourceLocation.actionIndex, 1);
      if (!draggedAction) return null;

      let targetWorkflowId = null;
      let targetIndex = null;

      if (target.type === 'workflow') {
        targetWorkflowId = Number(target.workflowId);
        const targetWorkflow = (hierarchyClone.workflows || []).find((workflow) => Number(workflow.id) === targetWorkflowId);
        if (!targetWorkflow) return null;
        targetIndex = targetWorkflow.actions.length;
      } else {
        return null;
      }

      const targetWorkflow = (hierarchyClone.workflows || []).find((workflow) => Number(workflow.id) === targetWorkflowId);
      if (!targetWorkflow) return null;

      targetIndex = Math.max(0, Math.min(targetIndex, targetWorkflow.actions.length));
      draggedAction.workflow_id = targetWorkflowId;
      targetWorkflow.actions.splice(targetIndex, 0, draggedAction);

      return {
        hierarchy: hierarchyClone,
        targetWorkflowId,
        targetIndex,
        sourceWorkflowId: Number(sourceLocation.workflowId),
        sourceIndex: sourceLocation.actionIndex,
      };
    },
    async persistActionMove(sourceWorkflowId, actionId, targetWorkflowId, targetOrder) {
      if (Number(sourceWorkflowId) !== Number(targetWorkflowId)) {
        await apiService.updateResource(
          API_ENDPOINTS.action(actionId),
          { workflow_id: Number(targetWorkflowId) },
          this.loggedInUser?.id,
          this.loggedInUser?.entra_id
        );
        return;
      }

      await apiService.createResource(
        API_ENDPOINTS.workflowActionMove(sourceWorkflowId, actionId),
        { order: targetOrder },
        this.loggedInUser?.entra_id
      );
    },
    async moveActionWithinWorkflow(workflow, action, direction) {
      if (this.isPersistingActionMove || !workflow || !action) return;

      const workflowId = Number(workflow.id);
      const actionId = Number(action.id);
      const workflowRef = (this.hierarchy.workflows || []).find((item) => Number(item.id) === workflowId);
      if (!workflowRef || !Array.isArray(workflowRef.actions)) return;

      const currentIndex = workflowRef.actions.findIndex((item) => Number(item.id) === actionId);
      if (currentIndex === -1) return;

      const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      if (targetIndex < 0 || targetIndex >= workflowRef.actions.length) return;

      const previousHierarchy = JSON.parse(JSON.stringify(this.hierarchy));
      const nextHierarchy = JSON.parse(JSON.stringify(this.hierarchy));
      const nextWorkflow = (nextHierarchy.workflows || []).find((item) => Number(item.id) === workflowId);
      if (!nextWorkflow) return;

      const [movedAction] = nextWorkflow.actions.splice(currentIndex, 1);
      if (!movedAction) return;
      nextWorkflow.actions.splice(targetIndex, 0, movedAction);

      this.hierarchy = nextHierarchy;
      this.clearActionNodePosition(actionId);
      this.isPersistingActionMove = true;

      try {
        await this.persistActionMove(workflowId, actionId, workflowId, targetIndex + 1);
        this.$nextTick(() => this.queueConnectorUpdate());
      } catch (error) {
        console.error('Failed to reorder activity within workflow:', error);
        this.hierarchy = previousHierarchy;
      } finally {
        this.isPersistingActionMove = false;
      }
    },
    async handleActionDrop(event) {
      const state = this.dragState;
      if (!state || state.type !== 'action' || !state.meta) return false;

      const actionId = Number(state.meta.actionId);
      const target = this.detectActionDropTarget(event);
      const sourceWorkflowId = Number(state.meta.workflowId);
      const originalHierarchy = state.meta.originalHierarchy || this.hierarchy;

      if (!target) {
        this.hierarchy = JSON.parse(JSON.stringify(originalHierarchy));
        this.resetActionDragPosition(state);
        return false;
      }

      const preview = this.buildActionMovePreview(originalHierarchy, actionId, target);
      if (!preview) {
        this.hierarchy = JSON.parse(JSON.stringify(originalHierarchy));
        this.resetActionDragPosition(state);
        return false;
      }

      this.hierarchy = preview.hierarchy;
      this.clearActionNodePosition(actionId);

      if (Number(preview.targetWorkflowId) === Number(sourceWorkflowId)) {
        this.hierarchy = JSON.parse(JSON.stringify(originalHierarchy));
        this.resetActionDragPosition(state);
        return false;
      }

      this.isPersistingActionMove = true;

      try {
        await this.persistActionMove(
          sourceWorkflowId,
          actionId,
          preview.targetWorkflowId,
          preview.targetIndex + 1
        );
        this.$nextTick(() => this.queueConnectorUpdate());
        return true;
      } catch (error) {
        console.error('Failed to move/reorder activity:', error);
        this.hierarchy = JSON.parse(JSON.stringify(originalHierarchy));
        this.resetActionDragPosition(state);
        return false;
      } finally {
        this.isPersistingActionMove = false;
      }
    },
    async handleExperimentDrop(event) {
      const state = this.dragState;
      if (!state || !state.meta) return false;

      const sourceActionId = state.meta.sourceActionId;
      const experimentId = Number(state.meta.experimentId);
      const target = this.detectExperimentDropTarget(event, state);
      const sourceType = sourceActionId == null ? 'standalone' : 'action';

      if (!target) {
        this.restoreDragPosition(state);
        return false;
      }

      if ((target.type === 'standalone' && sourceType === 'standalone') ||
          (target.type === 'action' && Number(target.actionId) === Number(sourceActionId))) {
        this.restoreDragPosition(state);
        return false;
      }

      const previousHierarchy = JSON.parse(JSON.stringify(this.hierarchy));
      const nextHierarchy = JSON.parse(JSON.stringify(this.hierarchy));

      let experiment = null;

      if (sourceType === 'standalone') {
        const sourceIndex = (nextHierarchy.unassigned_experiments || []).findIndex((item) => Number(item.id) === experimentId);
        if (sourceIndex === -1) {
          this.restoreDragPosition(state);
          return false;
        }
        experiment = nextHierarchy.unassigned_experiments.splice(sourceIndex, 1)[0];
      } else {
        const sourceAction = this.findActionById(sourceActionId, nextHierarchy);
        if (!sourceAction) {
          this.restoreDragPosition(state);
          return false;
        }
        const sourceIndex = (sourceAction.experiments || []).findIndex((item) => Number(item.id) === experimentId);
        if (sourceIndex === -1) {
          this.restoreDragPosition(state);
          return false;
        }
        experiment = sourceAction.experiments.splice(sourceIndex, 1)[0];
      }

      if (!experiment) {
        this.restoreDragPosition(state);
        return false;
      }

      if (target.type === 'standalone') {
        experiment.action_id = null;
        if (this.isDynamicStructureHierarchy) {
          experiment.project_structure_element_id = null;
        }
        nextHierarchy.unassigned_experiments = [...(nextHierarchy.unassigned_experiments || []), experiment];
      } else {
        const targetAction = this.findActionById(target.actionId, nextHierarchy);
        if (!targetAction) {
          this.restoreDragPosition(state);
          return false;
        }
        if (this.isDynamicStructureHierarchy) {
          experiment.project_structure_element_id = Number(targetAction.__elementId || target.actionId);
          experiment.action_id = null;
        } else {
          experiment.action_id = Number(target.actionId);
        }
        targetAction.experiments = [...(targetAction.experiments || []), experiment];
      }

      this.hierarchy = nextHierarchy;
      this.clearExperimentNodePositions(experimentId);
      this.isPersistingExperimentMove = true;

      try {
        await apiService.updateResource(
          API_ENDPOINTS.experiment(experiment.id),
          experiment,
          this.loggedInUser?.id,
          this.loggedInUser?.entra_id
        );

        this.$nextTick(() => this.queueConnectorUpdate());
        return true;
      } catch (error) {
        console.error('Failed to move experiment between activities:', error);
        this.hierarchy = previousHierarchy;
        this.restoreDragPosition(state);
        return false;
      } finally {
        this.isPersistingExperimentMove = false;
      }
    },
    queueConnectorUpdate() {
      if (this.connectorRaf) return;
      this.connectorRaf = requestAnimationFrame(() => {
        this.connectorRaf = null;
        this.updateConnectorLines();
      });
    },
    updateConnectorLines() {
      if (!this.hasWorkflows) {
        this.connectorLines = [];
        return;
      }
      const sceneSize = this.sceneDimension();
      this.overlaySize = {
        width: sceneSize,
        height: sceneSize,
      };
      const projectMetrics = this.projectSceneMetrics();
      const startX = projectMetrics.left + (projectMetrics.width / 2);
      const startY = projectMetrics.top + projectMetrics.height;

      const lines = [];

      (this.hierarchy.workflows || []).forEach((workflow, index) => {
        const workflowMetrics = this.workflowMetrics(workflow, index);
        const endX = workflowMetrics.left + (workflowMetrics.width / 2);
        const endY = workflowMetrics.top;

        lines.push({
          id: `wf-line-${workflow.id}`,
          x1: startX,
          y1: startY,
          x2: endX,
          y2: endY,
        });
      });

      this.connectorLines = lines;
    },
    onActionMouseDown(workflow, action, event) {
      if (this.isDynamicStructureHierarchy) {
        return;
      }
      this.beginDrag('action', `${workflow.id}-${action.id}`, event);
    },
    resetView() {
      this.$nextTick(() => {
        const frame = this.$refs.sceneFrameRef;
        const sceneSize = this.sceneDimension();

        if (!frame || !sceneSize) {
          this.zoom = 1;
          this.panX = 0;
          this.panY = 0;
          return;
        }

        const rect = frame.getBoundingClientRect();
        const padding = 50;
        const fitZoom = this.clamp(
          Math.min((rect.width - padding * 2) / sceneSize, (rect.height - padding * 2) / sceneSize),
          0.35,
          2.2
        );

        this.zoom = fitZoom;
        this.panX = (rect.width - (sceneSize * fitZoom)) / 2;
        this.panY = (rect.height - (sceneSize * fitZoom)) / 2;
      });
    },
    startPanDrag(event) {
      if (event.button !== 0 || this.dragState) return;

      this.panDragState = {
        startClientX: event.clientX,
        startClientY: event.clientY,
        originPanX: this.panX,
        originPanY: this.panY,
      };
    },
    handleGlobalMouseMove(event) {
      if (this.dragState) {
        this.onDrag(event);
        return;
      }

      if (!this.panDragState) return;

      const dx = event.clientX - this.panDragState.startClientX;
      const dy = event.clientY - this.panDragState.startClientY;
      this.panX = this.panDragState.originPanX + dx;
      this.panY = this.panDragState.originPanY + dy;
    },
    async handleGlobalMouseUp(event) {
      const dragState = this.dragState;
      if (dragState && (dragState.type === 'unassignedExperiment' || dragState.type === 'actionExperiment')) {
        await this.handleExperimentDrop(event);
      } else if (dragState && dragState.type === 'action') {
        await this.handleActionDrop(event);
      }

      this.dragState = null;
      this.panDragState = null;
    },
    handleWheelZoom(event) {
      const frame = this.$refs.sceneFrameRef;
      if (!frame) return;

      const rect = frame.getBoundingClientRect();
      const localX = event.clientX - rect.left;
      const localY = event.clientY - rect.top;
      const sceneX = (localX - this.panX) / this.zoom;
      const sceneY = (localY - this.panY) / this.zoom;
      const factor = event.deltaY < 0 ? 1.12 : 0.9;
      const nextZoom = this.clamp(this.zoom * factor, 0.35, 2.2);

      this.zoom = nextZoom;
      this.panX = localX - (sceneX * nextZoom);
      this.panY = localY - (sceneY * nextZoom);
    },
    close() {
      if (this.showEntityInfoModal) {
        this.closeEntityInfoModal();
        return;
      }
      if (this.showCreateExperimentModal) {
        this.closeCreateExperimentModal();
        return;
      }
      if (this.showCreateActionModal) {
        this.closeCreateActionModal();
        return;
      }
      if (this.showCreateWorkflowModal) {
        this.closeCreateWorkflowModal();
        return;
      }
      this.$emit('close');
    },
    onEscape(event) {
      if (event.key === 'Escape') {
        this.close();
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/assets/variables.scss';

.hierarchy-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  justify-content: center;
  align-items: stretch;
}

.hierarchy-card {
  width: 100%;
  height: 100%;
  background: $secondary-color;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.hierarchy-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);

  h3 {
    margin: 0;
    color: $primary-color;
  }

  p {
    margin: 2px 0 0;
    color: #333;
  }
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.btn-secondary {
  border: 1px solid rgba($primary-color, 0.25);
  background: #fff;
  color: $primary-color;
  border-radius: 8px;
  padding: 8px 12px;
  cursor: pointer;
}

.btn-primary {
  border: 1px solid rgba($primary-color, 0.18);
  background: $primary-color;
  color: #fff;
  border-radius: 8px;
  padding: 8px 14px;
  cursor: pointer;
}

.btn-primary:disabled,
.btn-secondary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.zoom-indicator {
  color: $primary-color;
  font-weight: 600;
}

.hierarchy-body {
  position: relative;
  flex: 1;
  overflow: hidden;
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.scene-frame {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  cursor: grab;
  border-radius: 12px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.36) 0%, rgba(255, 255, 255, 0.14) 100%);
}

.scene-frame.is-panning,
.scene-frame.is-interacting {
  cursor: grabbing;
}

.scene-root {
  position: relative;
  transform-origin: top left;
  will-change: transform;
}

.connector-overlay {
  position: absolute;
  left: 0;
  top: 0;
  pointer-events: none;
  z-index: 0;
}

.connector-line {
  stroke: rgba($primary-color, 0.45);
  stroke-width: 2;
}

.scene-area {
  position: relative;
  z-index: 1;
  flex: 0 0 auto;
  margin: 24px auto;
}

.project-circle {
  border-radius: 44px;
  border: 2px solid rgba($primary-color, 0.9);
  display: flex;
  flex-direction: column;
  position: relative;
  align-items: center;
  justify-content: flex-start;
  text-align: center;
  padding: 26px 30px;
  font-weight: 600;
  background: linear-gradient(180deg, #dce9f7 0%, #c8d9ef 100%);
  border-color: #6c8fb3;
}

.project-add-btn {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 2;
  border: none;
  background: $primary-color;
  color: #fff;
  border-radius: 8px;
  padding: 0;
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.project-name {
  color: $primary-color;
  margin-bottom: 14px;
  font-size: 18px;
}

.entity-name-btn {
  border: none;
  background: transparent;
  cursor: pointer;
  font-weight: 600;
  padding: 0;
}

.entity-name-btn:hover {
  text-decoration: underline;
}

.workflow-name,
.action-name {
  background: transparent;
}

.experiment-name-btn {
  width: 100%;
  color: inherit;
  font-size: inherit;
  line-height: inherit;
  text-align: center;
}

.entity-info-overlay {
  position: fixed;
  inset: 0;
  z-index: 2100;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.entity-info-card {
  width: min(760px, 100%);
  max-height: calc(100vh - 40px);
  overflow: auto;
  background: $secondary-color;
  border-radius: 12px;
  border: 1px solid rgba($primary-color, 0.2);
}

.entity-info-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 18px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);

  h3 {
    margin: 0;
    color: $primary-color;
  }
}

.entity-info-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.entity-nav-btn {
  min-width: 30px;
  padding: 0 6px;
  font-weight: 700;
}

.entity-info-body {
  padding: 16px 18px 20px;
}

.entity-info-fields {
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
  margin-bottom: 16px;
}

.entity-comments-section h4 {
  margin: 0 0 10px;
  color: $primary-color;
}

.entity-comments-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.entity-comment-item {
  background: #fff;
  border: 1px solid rgba($primary-color, 0.12);
  border-radius: 8px;
  padding: 10px 12px;
}

.entity-comment-meta {
  color: $primary-color;
  font-size: 12px;
  margin-bottom: 4px;
}

.entity-comment-text {
  color: #333;
}

.experiment-circles {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 12px;
}

.project-experiments {
  width: 100%;
  max-width: 300px;
  display: grid;
  grid-template-columns: repeat(3, 92px);
  justify-content: center;
  align-content: start;
  gap: 12px;
  box-sizing: border-box;
}

.experiment-circle {
  position: relative;
  width: 92px;
  height: 92px;
  border-radius: 50%;
  background: linear-gradient(180deg, #fffaf2 0%, #f2eadb 100%);
  border: 2px solid #b99f79;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 8px;
  font-size: 12px;
  line-height: 1.2;
}

.experiment-circle.tiny {
  width: 72px;
  height: 72px;
  font-size: 11px;
}

.workflow-circle {
  position: relative;
  z-index: 1;
  border-radius: 44px;
  border: 2px solid #7ba287;
  background: linear-gradient(180deg, #e3f1e7 0%, #cfe3d3 100%);
  padding: 20px;
  box-sizing: border-box;
  overflow: visible;
}

.action-add-btn {
  position: absolute;
  top: 14px;
  right: 10px;
  z-index: 2;
  border: none;
  background: $primary-color;
  color: #fff;
  border-radius: 8px;
  padding: 0;
  width: 26px;
  height: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 12px;
}

.workflow-add-btn {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 2;
  border: none;
  background: $primary-color;
  color: #fff;
  border-radius: 8px;
  padding: 0;
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.workflow-name {
  font-weight: 600;
  text-align: center;
  margin-bottom: 12px;
  color: $primary-color;
}

.action-list {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  padding-right: 42px;
  gap: 10px;
}

.action-circle {
  flex: 0 0 auto;
  align-self: center;
  transform: translateX(-22px);
  position: relative;
  z-index: 1;
  border: 2px solid #9a88bb;
  border-radius: 44px;
  background: linear-gradient(180deg, #efe7f8 0%, #ddd0ee 100%);
  padding: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  box-sizing: border-box;
}

.action-controls {
  position: absolute;
  right: -44px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.action-controls.hidden {
  opacity: 0;
  pointer-events: none;
}

.action-arrow {
  width: 18px;
  height: 26px;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.action-arrow:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.action-name {
  font-weight: 600;
  margin-bottom: 8px;
  font-size: 13px;
  color: $primary-color;
}

.action-experiments {
  width: 240px;
  max-width: 100%;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
}

.workflow-circle.drag-source-workflow {
  z-index: 80;
}

.action-circle.drag-source-action {
  z-index: 110;
}

.experiment-circle.dragging {
  z-index: 90;
}

.unassigned-experiment-circle {
  background: linear-gradient(180deg, #fbe9de 0%, #f3d3be 100%);
  border-color: #cb9471;
}

.action-experiment-circle {
  background: linear-gradient(180deg, #f6efc9 0%, #eadf9f 100%);
  border-color: #b8aa62;
}

.draggable-node {
  cursor: grab;
  user-select: none;
}

.draggable-node:active {
  cursor: grabbing;
}

.empty-state,
.state {
  text-align: center;
  color: #555;
}

.error {
  color: #b00020;
}

</style>
